import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, XAxis, YAxis } from 'recharts';

import { ReactComponent as AlertIcon } from '../../../../../assets/icons/alert-icon.svg';
import { ReactComponent as BarChartIcon } from '../../../../../assets/icons/device-management/bar-chart.svg';
import DefaultApiLimit from '../../../../../config/defaultLimit';
import currentTimeZone from '../../../../../enum/timeZone';
import {
  DeviceAlertsProperties,
  DeviceAlertsRequest,
} from '../../../../../interface/device-alerts';
import { ToggleListProperties } from '../../../../../interface/device-management.interface';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import {
  fetchDeviceAlerts,
  fetchListAlertsFilters,
  setSortData,
  updateAlertsFilters,
} from '../../../../../redux/slices/DeviceAlertsSlice';
import CustomTable from '../../../../../shared-components/custom-table/CustomTable';
import DurationToggle from '../../../../../shared-components/duration-toggle/DurationToggle';
import ToggleWithIcons from '../../../../../shared-components/toggle-with-icons/ToggleWithIcons';
import { GetParametersFromCommonFilters } from '../../../../../shared-functions/CommonFilters';
import { capitalizeFirstLetter } from '../../../../../shared-functions/StringFunctions';
import device from '../../../../../utils/api/device';

type PieChartDataType = {
  name: string;
  value: number;
};

interface BarChartDataType {
  date: string;
  label?: string;
  month?: string;
  maintenanceAlert: number;
  transactionAlert: number;
  performanceAlert: number;
  totalCount?: number;
  year?: number;
  hour: number;
}

/**
 * Function to fill missing months in chart
 * @param {any} data - The data
 * @returns {any} The result
 */
function fillMissingMonths(data: BarChartDataType[]) {
  const today = new Date();
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  // Generate past 12 months dynamically
  const past12Months = [];
  for (let index = 0; index < 12; index += 1) {
    const date = new Date(today.getFullYear(), today.getMonth() - index, 1); // Get the first day of the month
    past12Months.push({
      label: months[date.getMonth()], // Month name
      year: date.getFullYear(), // Year
    });
  }

  // Map input data for easy lookup
  const maintenanceAlertMap = new Map(data.map((item) => [item.month, item.maintenanceAlert]));
  const performanceAlertMap = new Map(data.map((item) => [item.month, item.performanceAlert]));
  const transactionAlertMap = new Map(data.map((item) => [item.month, item.transactionAlert]));

  // Generate result array with missing months filled
  const result = past12Months.map(({ label, year }) => {
    const key = label; // Use the month name as key
    return {
      hour: 0,
      date: '',
      label: `${label} ${year}`, // Combine month and year for output
      maintenanceAlert: maintenanceAlertMap.get(key) || 0, // Default to 0 if month is missing
      transactionAlert: transactionAlertMap.get(key) || 0, // Default 0
      performanceAlert: performanceAlertMap.get(key) || 0, // Default 0
    };
  });

  return result.reverse();
}

/**
 * Function to generate the map data for past 30 days
 * @param {any} data - The data
 * @returns {any} The result
 */
function fillMissingLastMonth(data: BarChartDataType[]) {
  const today = new Date();
  const past30Days = [];

  // Generate the past 30 days as ISO date strings
  for (let index = 0; index < 30; index += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - index);
    const isoDate = date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    past30Days.push({ date, isoDate });
  }

  // Map input data using full ISO date for lookup
  const maintenanceAlertMap = new Map(
    data.map((item) => [item.date.split('T')[0], item.maintenanceAlert]),
  );
  const transactionAlertMap = new Map(
    data.map((item) => [item.date.split('T')[0], item.transactionAlert]),
  );
  const performanceAlertMap = new Map(
    data.map((item) => [item.date.split('T')[0], item.performanceAlert]),
  );

  // Date formatter to get "Mar 18" format
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  });

  // Build the result array
  const result = past30Days.map(({ date, isoDate }) => ({
    hour: 0,
    date: dateFormatter.format(date),
    label: dateFormatter.format(date), // Format label as "Mar 18"
    maintenanceAlert: maintenanceAlertMap.get(isoDate) || 0, // Default to 0
    performanceAlert: performanceAlertMap.get(isoDate) || 0, // Default to 0
    transactionAlert: transactionAlertMap.get(isoDate) || 0, // Default to 0
  }));

  return result.reverse();
}

/**
 * Function to add missing data in last 7 days
 * @param {any} data - The data
 * @returns {any} The result
 */
function fillMissingLast7Days(data: BarChartDataType[]) {
  const today = new Date();
  const last7Days = [];

  // Generate the last 7 days as ISO date strings
  for (let index = 0; index < 7; index += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - index);
    last7Days.push(date);
  }

  // Map input data using full ISO date for lookup
  const maintenanceAlertMap = new Map(
    data.map((item) => [item.date.split('T')[0], item.maintenanceAlert]),
  );
  const transactionAlertMap = new Map(
    data.map((item) => [item.date.split('T')[0], item.transactionAlert]),
  );
  const performanceAlertMap = new Map(
    data.map((item) => [item.date.split('T')[0], item.performanceAlert]),
  );

  // Date formatter to get "Nov 14" format
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  });

  // Build the result array
  const result = last7Days.map((date) => {
    const isoDate = date.toISOString().split('T')[0]; // Use ISO date for lookup
    return {
      hour: 0,
      date: dateFormatter.format(date),
      label: dateFormatter.format(date), // Format label as "Mar 18"
      maintenanceAlert: maintenanceAlertMap.get(isoDate) || 0, // Default to 0
      performanceAlert: performanceAlertMap.get(isoDate) || 0, // Default to 0
      transactionAlert: transactionAlertMap.get(isoDate) || 0, // Default to 0
    };
  });

  return result.reverse();
}

/**
 * Function to handle missing data for the last 24 hours based on the current time zone
 * @param {any} data - The input data
 * @returns {any} The result with complete data for the last 24 hours
 */
function fillMissingLast24HrsData(data: BarChartDataType[]) {
  const now = new Date();
  const past24Hours = [];

  for (let index = 0; index < 24; index += 1) {
    const date = new Date(now);
    date.setHours(now.getHours() - index, 0, 0, 0); // Adjust hour, reset minutes/seconds/ms

    // Format date and hour based on the specified time zone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: currentTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23', // Ensures 0-23 hour format
    });

    const formattedDate = formatter.formatToParts(date);
    const year = formattedDate.find((p) => p.type === 'year')?.value;
    const month = formattedDate.find((p) => p.type === 'month')?.value;
    const day = formattedDate.find((p) => p.type === 'day')?.value;
    const hour = formattedDate.find((p) => p.type === 'hour')?.value;

    // Ensure YYYY-MM-DD format
    const isoDate = `${year}-${month}-${day}`;

    past24Hours.push({ date: isoDate, hour: Number(hour) });
  }

  // Build the result array
  const result = past24Hours.map((eachHour) => {
    const dataForHour = data.find(
      (eachData) => eachData.date === eachHour.date && eachData.hour === eachHour.hour,
    );

    return {
      hour: 0,
      date: '',
      label: eachHour.hour.toString(), // Hour (0-23)
      maintenanceAlert: dataForHour ? dataForHour.maintenanceAlert : 0, // Default to 0 if data not found
      transactionAlert: dataForHour ? dataForHour.transactionAlert : 0, // Default to 0 if data not found
      performanceAlert: dataForHour ? dataForHour.performanceAlert : 0, // Default to 0 if data not found
    };
  });

  return result.reverse(); // Ensuring chronological order
}

/**
 * Component to display device alert and device-chart
 * @returns {any} The result
 */
function Alerts() {
  const { t } = useTranslation('devices');
  const dispatch = useAppDispatch();
  const {
    alerts,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filterListLoading,
    filters,
    sortField,
    sortBy,
  } = useAppSelector((s) => s.deviceAlert);
  const { selectedChargingPoint } = useAppSelector((s) => s.device);

  const barChartContainerReference = useRef<HTMLDivElement>(null);
  const [barChartSize, setBarChartSize] = useState({
    width: 0,
    height: 0,
  });
  const [chartToggle, setChartToggle] = useState([
    {
      svgIcon: <AlertIcon />,
      key: 'pie-chart',
      isActive: false,
      label: '',
    },
    {
      svgIcon: <BarChartIcon />,
      key: 'bar-chart',
      isActive: true,
      label: '',
    },
  ]);

  /**
   * Fetch the account details in redus
   * @param {any} data - The data
   */
  const fetchData = (data: DeviceAlertsRequest) => {
    if (!loading) {
      dispatch(fetchDeviceAlerts(data));
    }
  };
  const column = [
    {
      label: t('date'),
      accessorKey: 'date' as keyof DeviceAlertsProperties,
      sortAllow: true,
      columnWidth: 40,
    },
    {
      label: t('time'),
      accessorKey: 'time' as keyof DeviceAlertsProperties,
      sortAllow: true,
      columnWidth: 40,
    },
    {
      label: t('alerts'),
      accessorKey: 'message' as keyof DeviceAlertsProperties,
      /**
       * Custom table column design
       * @param {any} currentRow - The currentRow
       * @returns {any} The result
       */
      customData: (currentRow: DeviceAlertsProperties) =>
        currentRow.message ? (
          <div className="default-alert-column">
            <span>
              <AlertIcon />
            </span>
            <p>{currentRow.message}</p>
          </div>
        ) : (
          <div />
        ),
      sortAllow: true,
    },
  ];

  const [toggleList, setToggleList] = useState<ToggleListProperties[]>([
    {
      label: t('last24Hours'),
      key: 'last24hours',
      isActive: true,
    },
    {
      label: t('last7Days'),
      key: 'last7days',
      isActive: false,
    },
    {
      label: t('lastMonth'),
      key: 'lastMonth',
      isActive: false,
    },
    {
      label: t('last12Months'),
      key: 'last12months',
      isActive: false,
    },
  ]);

  const [pieChartData, setPieChartData] = useState<PieChartDataType[]>([
    { name: 'maintenance', value: 0 },
    { name: 'transaction', value: 0 },
    { name: 'energy', value: 0 },
    { name: 'performance', value: 0 },
  ]);

  const categories: (keyof Omit<BarChartDataType, 'month'>)[] = [
    'maintenanceAlert',
    'transactionAlert',
    'performanceAlert',
  ];

  const [barGraphData, setBarGraphData] = useState<BarChartDataType[]>([]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  /**
   * Function to change the active menu
   * @param {any} key - The key
   */
  const handleClickFunction = (key: string) => {
    setToggleList((previousValue) =>
      previousValue.map((eachValue) => {
        let updatedValue = eachValue;
        if (eachValue.key === key) {
          updatedValue = {
            ...eachValue,
            isActive: true,
          };
        } else {
          updatedValue = {
            ...eachValue,
            isActive: false,
          };
        }
        return updatedValue;
      }),
    );
  };

  /**
   * Function to switch the tab
   * @param {string} key - The key
   */
  const handleClickToggle = (key: string) => {
    setChartToggle((previousTabs) =>
      previousTabs.map((tab) => {
        if (tab.key === key) {
          return {
            ...tab,
            isActive: true,
          };
        }
        return {
          ...tab,
          isActive: false,
        };
      }),
    );
  };

  /**
   * Function to fetch report for charging schedule
   * @param {any} timePeriod - The timePeriod
   */
  const handleFetchReport = async (timePeriod: string) => {
    try {
      const response = await device.fetchAlertReport({
        chargingPointSn: selectedChargingPoint?.chargingPointSn,
        timeZone: currentTimeZone,
        timePeriod,
      });
      let finalData: BarChartDataType[] = [];
      if (timePeriod === 'last12months') {
        finalData = fillMissingMonths(response.data);
      } else if (timePeriod === 'lastMonth') {
        finalData = fillMissingLastMonth(response.data);
      } else if (timePeriod === 'last7days') {
        finalData = fillMissingLast7Days(response.data);
      } else if (timePeriod === 'last24hours') {
        finalData = fillMissingLast24HrsData(response.data);
      }
      console.log(finalData);
      setBarGraphData(finalData);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    handleFetchReport(toggleList.find((toggle) => toggle.isActive === true)?.key || 'last24hours');
  }, [toggleList]);

  useEffect(() => {
    const width = barChartContainerReference.current?.offsetWidth || 0;
    const height = barChartContainerReference.current?.offsetHeight || 0;
    setBarChartSize({
      width: width - 25,
      height,
    });
  }, [barChartContainerReference.current]);

  useEffect(() => {
    const updatedPieChartData = categories.map((category) => ({
      name: category,
      value: barGraphData.reduce((sum, data) => sum + Number(data[category] ?? 0), 0),
    }));

    setPieChartData(updatedPieChartData);
  }, [barGraphData]);

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
      chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
    });
  };

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageNateLimitChange = (value: DeviceAlertsRequest) => {
    fetchData({
      ...value,
      sortField,
      sortBy,
      chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
    });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchData({
      page: DefaultApiLimit.page,
      limit,
      chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
      ...queryParameters,
    });
  }, [filters, selectedChargingPoint]);

  return (
    <div
      style={{
        marginTop: '20%',
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex',
      }}>
      <h1>Comming soon...</h1>
    </div>
  );

  return (
    <div className="device-alerts-wrap">
      <div className="device-alerts-table">
        <CustomTable
          header={column}
          data={alerts}
          serialNo
          limit={limit}
          page={page}
          loading={loading}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={(value: any) => pageNateLimitChange(value)}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updateAlertsFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListAlertsFilters}
          version="v2"
          height={400}
        />
      </div>
      <div className="device-chart-and-info">
        <div className="device-chart-wrap">
          <div className="chart-header">
            <h6>{t('alerts')}</h6>
            <div className="chart-duration-and-toggle">
              <DurationToggle toggleList={toggleList} handleClickFunction={handleClickFunction} />
              <ToggleWithIcons
                leftMenu={chartToggle[0]}
                rightMenu={chartToggle[1]}
                handleClickToggle={handleClickToggle}
              />
            </div>
          </div>
          <div className="device-chart-container">
            {chartToggle[0].isActive ? (
              <div className="pie-chart-wrap">
                <div className="pie-chart-container">
                  <PieChart width={250} height={250}>
                    <Pie
                      data={pieChartData}
                      cx={125}
                      cy={125}
                      innerRadius={50}
                      outerRadius={80}
                      fill="#8884d8"
                      paddingAngle={2}
                      dataKey="value">
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                  <div className="total-sessions-wrap">
                    <p>
                      {t('total')} <br /> {t('sessions')}
                    </p>
                    <span>{pieChartData.reduce((sum, item) => sum + item.value, 0)}</span>
                  </div>
                </div>
                <div className="pie-chart-label">
                  {pieChartData.map((eachData, index) => (
                    <div className="each-label">
                      <div
                        className="each-label-color"
                        style={{ '--label-color': COLORS[index] } as React.CSSProperties}
                      />
                      <p>{capitalizeFirstLetter(t(eachData.name))}</p>
                      <div className="each-label-value">
                        <span>{eachData.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bar-chart-wrap">
                <div className="bar-chart-container" ref={barChartContainerReference}>
                  <p className="bar-chart-title">{t('frequency')}</p>
                  <BarChart
                    width={barChartSize.width}
                    height={barChartSize.width / 2.5}
                    data={barGraphData}
                    margin={{
                      top: 50,
                      right: 0,
                      left: -10,
                      bottom: 5,
                    }}>
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0} />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                    <Bar dataKey="maintenanceAlert" fill="#FB497C" />
                    <Bar dataKey="transactionAlert" fill="#4ABDFB" />
                    {/* <Bar dataKey="energy" fill="#41DCE5" /> */}
                    <Bar dataKey="performanceAlert" fill="#FF7714" />
                  </BarChart>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* <div className="device-additional-info">
          <h6>Additional Info</h6>
        </div> */}
      </div>
    </div>
  );
}

export default Alerts;
